import React, { forwardRef, Fragment } from 'react';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { ajaxconfig } from "../ajaxconfig";
import { ToastContainer, toast } from 'react-toastify';
import { Sidebar } from '../dashboard_component';
import { upload_file, private_all_asset, private_delete_asset } from "../serverurl";

const CbtImgLibrary = () => {
    const [file, set_file] = useState(null);
    const [db_img_files, set_db_img_file] = useState([]);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [uploadEffect, setuploadEffect] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [currentImage, setCurrentImage] = useState(null);
    const [deleteImagesEffect, setDeleteImagesEffect] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('newest');
    const [selectedFile, setSelectedFile] = useState(null);
    
    const config = ajaxconfig();

    const handleToggle = () => {
        setIsCollapsed(!isCollapsed);
    };

    const refresh_img = () => {
        axios.get(private_all_asset, config).then((response) => {
            set_db_img_file(Array.isArray(response.data.img_files) ? response.data.img_files : []);
        }).catch((error) => {
            console.log(error);
            toast.error('Failed to load images');
        });
    };

    const upload_img = (e) => {
        e.preventDefault();
        setuploadEffect(true);
        
        if (!selectedFile) {
            toast.error("Please select an image file");
            setuploadEffect(false);
            return;
        }
    
        const formData = new FormData();
        formData.append('uploaded_file', selectedFile);

        const maxSize = 5 * 1024 * 1024; // 5MB
        if (selectedFile.size > maxSize) {
            toast.error("File size exceeds the 5MB limit");
            setuploadEffect(false);
            return;
        }
    
        axios.post(upload_file, formData, config)
            .then((response) => {
                setuploadEffect(false);
                setShowUploadModal(false);
                setSelectedFile(null);
                set_file(null);
                refresh_img();
                toast.success("Image uploaded successfully!");
            })
            .catch((error) => {
                setuploadEffect(false);
                if (error.response && error.response.status === 422) {
                    toast.error(error.response.data.message);
                } else {
                    toast.error("Upload failed. Please try again.");
                }
            });
    };

    const handlePreview = (image) => {
        setCurrentImage(image);
        setShowPreviewModal(true);
    };

    const deleteImage = () => {
        if (!currentImage) return;
        
        setDeleteImagesEffect(true);
        const datas = { file_name: currentImage.file_name };

        axios.post(private_delete_asset, datas, config)
            .then((response) => {
                setDeleteImagesEffect(false);
                setShowPreviewModal(false);
                setCurrentImage(null);
                refresh_img();
                toast.success("Image deleted successfully!");
            })
            .catch((error) => {
                setDeleteImagesEffect(false);
                toast.error(error.message || "Error deleting image");
            });
    };

    useEffect(() => {
        refresh_img();
    }, []);

    const previewImage = (e) => {
        const file = e.target.files[0];
        setSelectedFile(file);
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                set_file(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Filter and sort images
    const filteredImages = db_img_files
        .filter(img => 
            img.file_name.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .sort((a, b) => {
            switch (sortBy) {
                case 'newest':
                    return new Date(b.created_at || 0) - new Date(a.created_at || 0);
                case 'oldest':
                    return new Date(a.created_at || 0) - new Date(b.created_at || 0);
                case 'name':
                    return a.file_name.localeCompare(b.file_name);
                default:
                    return 0;
            }
        });

    const getFileSize = (bytes) => {
        if (!bytes) return 'Unknown';
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
    };

    return (
        <div className="flex min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
            <Sidebar isCollapsed={isCollapsed} onToggle={handleToggle} />
            
            <div className={`flex-1 transition-all duration-300 pt-16 lg:pt-5 ${isCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    {/* Header Section */}
                    <div className="mb-8">
                        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                                    <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    Image Library
                                </h1>
                                <p className="text-gray-600 mt-2">Manage and organize your image assets</p>
                            </div>
                            
                            <div className="flex items-center gap-3 bg-white rounded-xl shadow-sm px-4 py-3">
                                <div className="text-right">
                                    <p className="text-sm font-medium text-gray-900">Total Images</p>
                                    <p className="text-2xl font-bold text-blue-600">{db_img_files.length}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                        {/* Main Content */}
                        <div className="lg:col-span-3 space-y-6">
                            {/* Controls Card */}
                            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                                <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
                                    <div className="flex-1 w-full sm:max-w-md">
                                        <div className="relative">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            </svg>
                                            <input
                                                type="text"
                                                placeholder="Search images..."
                                                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="flex gap-3 w-full sm:w-auto">
                                        <select 
                                            className="px-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                            value={sortBy}
                                            onChange={(e) => setSortBy(e.target.value)}
                                        >
                                            <option value="newest">Newest First</option>
                                            <option value="oldest">Oldest First</option>
                                            <option value="name">Name A-Z</option>
                                        </select>
                                        
                                        <button 
                                            className="px-4 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 flex items-center gap-2 font-semibold hover:scale-105"
                                            onClick={() => setShowUploadModal(true)}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6h.1a5 5 0 011 9.9M12 12v6m0 0l-3-3m3 3l3-3" />
                                            </svg>
                                            Upload
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Images Grid */}
                            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                                <div className="p-6 border-b border-gray-100">
                                    <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        All Images ({filteredImages.length})
                                    </h2>
                                </div>
                                
                                <div className="p-6">
                                    {filteredImages.length > 0 ? (
                                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                                            {filteredImages.map((data) => (
                                                <ImgLoop 
                                                    key={data.id} 
                                                    data={data} 
                                                    onPreview={handlePreview} 
                                                />
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-12">
                                            <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                </svg>
                                            </div>
                                            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Images Found</h3>
                                            <p className="text-gray-500 mb-6">
                                                {searchTerm ? 'Try adjusting your search terms' : 'Get started by uploading your first image'}
                                            </p>
                                            <button 
                                                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 flex items-center gap-2 font-semibold hover:scale-105 mx-auto"
                                                onClick={() => setShowUploadModal(true)}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6h.1a5 5 0 011 9.9M12 12v6m0 0l-3-3m3 3l3-3" />
                                                </svg>
                                                Upload First Image
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Sidebar */}
                        <div className="space-y-6">
                            {/* Quick Stats */}
                            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                    Library Stats
                                </h3>
                                
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                        <span className="text-gray-600">Total Images</span>
                                        <span className="font-bold text-blue-600">{db_img_files.length}</span>
                                    </div>
                                    
                                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                        <span className="text-gray-600">Storage Used</span>
                                        <span className="font-medium text-gray-900">
                                            {getFileSize(db_img_files.reduce((acc, img) => acc + (img.file_size || 0), 0))}
                                        </span>
                                    </div>
                                    
                                    <div className="flex justify-between items-center py-2">
                                        <span className="text-gray-600">Max File Size</span>
                                        <span className="font-medium text-gray-900">5 MB</span>
                                    </div>
                                </div>
                            </div>

                            {/* Tips Card */}
                            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-100 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Quick Tips
                                </h3>
                                
                                <ul className="space-y-3 text-sm text-gray-600">
                                    <li className="flex items-start gap-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Supported formats: JPG, PNG, GIF, WebP
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Maximum file size: 5MB per image
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Click any image to preview and manage
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Upload Image Modal */}
            {showUploadModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
                        <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 rounded-t-2xl">
                            <div className="flex justify-between items-center">
                                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6h.1a5 5 0 011 9.9M12 12v6m0 0l-3-3m3 3l3-3" />
                                    </svg>
                                    Upload Image
                                </h3>
                                <button 
                                    onClick={() => {
                                        setShowUploadModal(false);
                                        setSelectedFile(null);
                                        set_file(null);
                                    }}
                                    className="text-white hover:text-gray-200 transition-colors"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                        
                        <div className="p-6">
                            <form onSubmit={upload_img}>
                                <div className="mb-6">
                                    <label className="block text-sm font-semibold text-gray-700 mb-3">Select Image File</label>
                                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-blue-500 transition-colors cursor-pointer">
                                        <input 
                                            type="file" 
                                            className="hidden"
                                            id="file-upload"
                                            accept="image/*" 
                                            onChange={previewImage} 
                                            required
                                        />
                                        <label htmlFor="file-upload" className="cursor-pointer">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6h.1a5 5 0 011 9.9M12 12v6m0 0l-3-3m3 3l3-3" />
                                            </svg>
                                            <p className="text-sm text-gray-600 mb-1">
                                                {selectedFile ? selectedFile.name : 'Click to select an image'}
                                            </p>
                                            <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
                                        </label>
                                    </div>
                                </div>
                                
                                {file && (
                                    <div className="mb-6">
                                        <label className="block text-sm font-semibold text-gray-700 mb-3">Preview</label>
                                        <div className="flex justify-center">
                                            <img 
                                                src={file} 
                                                alt="Preview" 
                                                className="max-h-48 rounded-lg border border-gray-200 shadow-sm" 
                                            />
                                        </div>
                                        {selectedFile && (
                                            <p className="text-xs text-gray-500 text-center mt-2">
                                                Size: {getFileSize(selectedFile.size)}
                                            </p>
                                        )}
                                    </div>
                                )}
                                
                                <div className="flex justify-end gap-3">
                                    <button 
                                        type="button"
                                        onClick={() => {
                                            setShowUploadModal(false);
                                            setSelectedFile(null);
                                            set_file(null);
                                        }}
                                        className="px-5 py-2.5 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="submit"
                                        disabled={uploadEffect || !selectedFile}
                                        className={`px-5 py-2.5 rounded-xl text-white flex items-center gap-2 transition-colors font-medium ${
                                            uploadEffect || !selectedFile 
                                                ? 'bg-gray-400 cursor-not-allowed' 
                                                : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                                        }`}
                                    >
                                        {uploadEffect ? (
                                            <>
                                                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Uploading...
                                            </>
                                        ) : (
                                            <>
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6h.1a5 5 0 011 9.9M12 12v6m0 0l-3-3m3 3l3-3" />
                                                </svg>
                                                Upload Image
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Preview Image Modal */}
            {showPreviewModal && currentImage && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex justify-between items-center">
                                <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                    Image Preview
                                </h3>
                                <button 
                                    onClick={() => {
                                        setShowPreviewModal(false);
                                        setCurrentImage(null);
                                    }}
                                    className="text-gray-500 hover:text-gray-700 transition-colors"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                        
                        <div className="p-6">
                            <div className="flex justify-center bg-gray-50 rounded-xl p-4">
                                <img 
                                    src={currentImage.file_name} 
                                    alt="Preview" 
                                    className="max-h-[60vh] max-w-full rounded-lg object-contain"
                                />
                            </div>
                            
                            <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                                <p className="text-sm font-medium text-gray-700 mb-2">File Information</p>
                                <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                                    <div>
                                        <span className="font-medium">Filename:</span>
                                        <p className="truncate">{currentImage.file_name.split('/').pop()}</p>
                                    </div>
                                    <div>
                                        <span className="font-medium">Size:</span>
                                        <p>{getFileSize(currentImage.file_size)}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                            <button 
                                onClick={() => {
                                    setShowPreviewModal(false);
                                    setCurrentImage(null);
                                }}
                                className="px-5 py-2.5 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                            >
                                Close
                            </button>
                            <button 
                                className="px-5 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={deleteImage}
                                disabled={deleteImagesEffect}
                            >
                                {deleteImagesEffect ? (
                                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                )}
                                {deleteImagesEffect ? 'Deleting...' : 'Delete Image'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer 
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </div>
    );
};

const ImgLoop = forwardRef(({ data, onPreview }, ref) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);

    return (
        <div 
            className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 hover:scale-105 overflow-hidden cursor-pointer group"
            onClick={() => onPreview(data)}
            ref={ref}
        >
            <div className="relative aspect-square bg-gray-100">
                {!imageLoaded && !imageError && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                    </div>
                )}
                {imageError ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                    </div>
                ) : (
                    <img 
                        className={`object-cover w-full h-full transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                        src={data.file_name} 
                        alt={data.file_name.split('/').pop()} 
                        onLoad={() => setImageLoaded(true)}
                        onError={() => setImageError(true)}
                    />
                )}
        
            </div>
            <div className="p-3 bg-white border-t border-gray-100">
                <p className="text-xs text-gray-600 truncate" title={data.file_name.split('/').pop()}>
                    {data.file_name.split('/').pop()}
                </p>
            </div>
        </div>
    );
});

export { CbtImgLibrary };